import type {
  CompleteOnboardingPayload,
  OnboardingCreationModule,
  UserProfile,
} from '@/lib/api/users';
import {
  CONVERSATION_CAPABILITY_CONTRACTS,
  CONVERSATION_TASK_FAMILY_DEFINITIONS,
  FEATURED_CONVERSATION_TASK_FAMILIES,
  type ConversationCapabilityKey,
  type ConversationTaskFamilyKey,
} from '@/features/conversation-capabilities/contract';

export const ONBOARDING_VERSION = 2 as const;
export const ONBOARDING_DRAFT_STORAGE_KEY = 'dobidobi:onboarding-draft:v2';
export const ONBOARDING_PENDING_START_STORAGE_KEY = 'dobidobi:onboarding-pending-start:v1';
export const ONBOARDING_START_MODULE_EVENT = 'dobidobi:onboarding-start-module';
export const ONBOARDING_CREATION_COMPLETED_EVENT = 'dobidobi:onboarding-creation-completed';
export const ONBOARDING_REOPEN_CREATION_GUIDE_EVENT = 'dobidobi:onboarding-reopen-creation-guide';

export type OnboardingRole = CompleteOnboardingPayload['role'];
export type OnboardingAiFamiliarity = NonNullable<CompleteOnboardingPayload['ai_familiarity']>;
export type OnboardingPrimaryGoal = CompleteOnboardingPayload['primary_goal'];
export type OnboardingProductCategory = CompleteOnboardingPayload['product_category'];
export type OnboardingMonthlyInvestment = NonNullable<CompleteOnboardingPayload['monthly_investment']>;
export type OnboardingDiscoverySource = CompleteOnboardingPayload['discovery_source'];

export interface OnboardingPreference {
  version?: number;
  status?: 'pending' | 'completed' | 'skipped';
  started_at?: string;
  completed_at?: string;
  role?: OnboardingRole;
  role_other?: string | null;
  ai_familiarity?: OnboardingAiFamiliarity;
  primary_goal?: OnboardingPrimaryGoal;
  product_category?: OnboardingProductCategory;
  product_category_other?: string | null;
  monthly_investment?: OnboardingMonthlyInvestment;
  discovery_source?: OnboardingDiscoverySource;
  discovery_source_other?: string | null;
  activation?: {
    status?: 'pending' | 'started' | 'completed' | 'dismissed';
    recommended_module?: OnboardingCreationModule;
    selected_module?: OnboardingCreationModule;
    updated_at?: string;
    completed_at?: string;
  };
}

export type { OnboardingCreationModule };

export interface OnboardingStartIntent {
  module: OnboardingCreationModule;
  capability: ConversationCapabilityKey;
  createdAt: number;
}

const ONBOARDING_START_INTENT_TTL_MS = 10 * 60 * 1000;
let pendingOnboardingStartIntent: OnboardingStartIntent | null = null;

export const ONBOARDING_MODULE_TASK_FAMILY = Object.fromEntries(
  FEATURED_CONVERSATION_TASK_FAMILIES.map((family) => {
    const creationModule = CONVERSATION_TASK_FAMILY_DEFINITIONS[family].onboardingModule;
    if (!creationModule) throw new Error(`Missing onboarding module for Explore family: ${family}`);
    return [creationModule, family];
  }),
) as Record<OnboardingCreationModule, Exclude<ConversationTaskFamilyKey, 'reference'>>;

export const ONBOARDING_MODULE_CAPABILITY = Object.fromEntries(
  Object.entries(ONBOARDING_MODULE_TASK_FAMILY).map(([module, family]) => [
    module,
    CONVERSATION_TASK_FAMILY_DEFINITIONS[family].onboardingDefaultCapability,
  ]),
) as Record<OnboardingCreationModule, ConversationCapabilityKey>;

function isValidOnboardingStartIntent(value: unknown): value is OnboardingStartIntent {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const candidate = value as Partial<OnboardingStartIntent>;
  if (
    typeof candidate.module !== 'string'
    || !(candidate.module in ONBOARDING_MODULE_TASK_FAMILY)
    || typeof candidate.capability !== 'string'
    || !(candidate.capability in CONVERSATION_CAPABILITY_CONTRACTS)
    || typeof candidate.createdAt !== 'number'
    || !Number.isFinite(candidate.createdAt)
  ) return false;

  const age = Date.now() - candidate.createdAt;
  if (age < -60_000 || age > ONBOARDING_START_INTENT_TTL_MS) return false;
  return CONVERSATION_CAPABILITY_CONTRACTS[candidate.capability as ConversationCapabilityKey].family
    === ONBOARDING_MODULE_TASK_FAMILY[candidate.module as OnboardingCreationModule];
}

export function writePendingOnboardingStartIntent(
  module: OnboardingCreationModule,
  capability: ConversationCapabilityKey,
): void {
  const intent: OnboardingStartIntent = { module, capability, createdAt: Date.now() };
  pendingOnboardingStartIntent = intent;
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(ONBOARDING_PENDING_START_STORAGE_KEY, JSON.stringify(intent));
  } catch {
    // 同页动态挂载仍可通过模块内存恢复；sessionStorage 只增强刷新后的可靠性。
  }
}

export function readPendingOnboardingStartIntent(): OnboardingStartIntent | null {
  let candidate: unknown = pendingOnboardingStartIntent;
  if (!candidate && typeof window !== 'undefined') {
    try {
      const stored = window.sessionStorage.getItem(ONBOARDING_PENDING_START_STORAGE_KEY);
      candidate = stored ? JSON.parse(stored) : null;
    } catch {
      candidate = null;
    }
  }
  if (isValidOnboardingStartIntent(candidate)) return candidate;
  clearPendingOnboardingStartIntent();
  return null;
}

export function clearPendingOnboardingStartIntent(): void {
  pendingOnboardingStartIntent = null;
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.removeItem(ONBOARDING_PENDING_START_STORAGE_KEY);
  } catch {
    // 内存状态已经清理，存储不可用时无需阻断创作流程。
  }
}

export function clearOnboardingStartQueryMarker(): void {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  if (!url.searchParams.has('onboarding')) return;
  url.searchParams.delete('onboarding');
  const nextUrl = `${url.pathname}${url.search}${url.hash}`;
  window.history.replaceState(window.history.state, '', nextUrl);
}

export function getOnboardingTaskFamily(
  module: OnboardingCreationModule,
): Exclude<ConversationTaskFamilyKey, 'reference'> {
  return ONBOARDING_MODULE_TASK_FAMILY[module];
}

export function getOnboardingModuleForTaskFamily(
  family: ConversationTaskFamilyKey,
): OnboardingCreationModule | null {
  return CONVERSATION_TASK_FAMILY_DEFINITIONS[family].onboardingModule;
}

export function getOnboardingModuleForCapability(
  capability: string | null | undefined,
): OnboardingCreationModule | null {
  const defaultEntry = Object.entries(ONBOARDING_MODULE_CAPABILITY).find(
    ([, value]) => value === capability,
  );
  if (defaultEntry) return defaultEntry[0] as OnboardingCreationModule;
  if (!capability || !(capability in CONVERSATION_CAPABILITY_CONTRACTS)) return null;
  return getOnboardingModuleForTaskFamily(
    CONVERSATION_CAPABILITY_CONTRACTS[capability as ConversationCapabilityKey].family,
  );
}

export function getOnboardingPreference(profile: UserProfile | null): OnboardingPreference | null {
  const value = profile?.preferences?.onboarding;
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }
  return value as OnboardingPreference;
}

export function isOnboardingRequired(profile: UserProfile | null): boolean {
  const onboarding = getOnboardingPreference(profile);
  return !(
    onboarding?.version === ONBOARDING_VERSION
    && (onboarding.status === 'completed' || onboarding.status === 'skipped')
  );
}

export function buildOnboardingRedirect(pathname: string, search = ''): string {
  const requestedPath = `${pathname || '/explore'}${search || ''}`;
  return `/onboarding?redirect=${encodeURIComponent(requestedPath)}`;
}
